import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataView } from "primereact/dataview";
import { Skeleton } from "primereact/skeleton";
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import React, { useContext, useEffect, useRef, useState } from "react";
import { createInstallation, updateTournee } from "../../../service/ScrbService";
import { allTournees, currentInstallation, currentTournee, extractTournees, loadCurrentInstallation, loadInstallations, parseDate, parseTime, scrollToTop, tourneeInstallations } from "../ScrbTools";
import ManageTournee from "./ManageTournee";
import { Calendar } from 'primereact/calendar';
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Sidebar } from 'primereact/sidebar';
import { addLocale } from 'primereact/api';
import InstallationCard from "../installation/InstallationCard";
import PropTypes from 'prop-types';
import { useGlobalHelpState } from "../../../help-context";
import { Help } from "../../help/Help";
import { StructureContext } from "../../../structure-context";
import Installation from "../installation/Installation";
import AddContactDialog from "../informations/AddContactDialog";

Tournees.propTypes = {
    loadTournees: PropTypes.func
};

function Tournees(props) {

    const structure = useContext(StructureContext);
    const installation = useHookstate(currentInstallation);
    const installations = useHookstate(tourneeInstallations);
    const selectedTournee = useHookstate(currentTournee);
    const tournees = useHookstate(allTournees);
    const [isLoading, setIsLoading] = useState(true);
    const toast = useRef(null);
    const helpState = useGlobalHelpState();
    const [tourneeDates, setTourneeDates] = useState([]);
    const [printMode, setPrintMode] = useState(null);
    const [filters, setFilters] = useState({
        date: null,
        status: '',
        search: ''
    });

    useEffect(() => {
        addLocale('fr', {
            firstDayOfWeek: 1,
            dayNames: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
            dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
            dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
            monthNames: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
            monthNamesShort: ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"],
            today: "Aujourd'hui",
            clear: "Effacer"
        });
        addLocale('de', {
            firstDayOfWeek: 1,
            dayNames: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
            dayNamesShort: ["Son", "Mon", "Die", "Mit", "Don", "Fre", "Sam"],
            dayNamesMin: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
            monthNames: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
            monthNamesShort: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
            today: "Heute",
            clear: "Löschen"
        });
        helpState.set({ show: false, help: Help.tournee })
        //eslint-disable-next-line
    }, [])

    useEffect(() => {
        loadTournees();
        // eslint-disable-next-line
    }, [installations]);

    useEffect(() => {
        if (tournees.get().length > 0) {
            tournees.get().forEach(tournee => {
                setTourneeDates(prevDates => [...prevDates, new Date(tournee.date).toDateString()]);
            });
        }
    }, [tournees])

    const loadTournees = () => {
        tournees.set(extractTournees(installations.get()));
        setIsLoading(false);
    }

    const saveTournee = async (tournee) => {
        setIsLoading(true);
        try {
            const response = await updateTournee(tournee);
            if (response.statusCode === 200) {
                props.loadTournees();
                showNotif("success", "Tournee sauvegardée", "Tournee sauvegardée avec succès");
                selectedTournee.set({});
            } else {
                showNotif("error", I18n.get("tourneeNotSaved"), "");
            }
        } catch (error) {
            showNotif("error", I18n.get("tourneeNotSaved"), "");
        } finally {
            setIsLoading(false);
        }
    }

    const showNotif = (severity, summary, detail) => {
        toast.current?.show({
            severity: `${severity}`,
            summary: `${summary}`,
            detail: `${detail}`,
            life: 3000
        });
    }

    const modifyTournee = (tournee) => {
        const tourneeModifiee = {
            ...tournee,
            meetings: tournee.meetings.map(meeting => ({
                ...meeting,
                recStatusId: 3
            }))
        };
        saveTournee(tourneeModifiee);
    }

    const valideTournee = (tournee) => {
        saveTournee({
            ...tournee,
            meetings: tournee.meetings.map(meeting => ({
                ...meeting,
                recStatusId: 5
            }))
        });
    }

    const confirmValideTournee = (tournee) => {
        confirmDialog({
            message: I18n.get("confirmValideTournee"),
            header: I18n.get("confirm"),
            defaultFocus: 'reject',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: I18n.get("validate"),
            rejectLabel: I18n.get("cancel"),
            accept: () => valideTournee(tournee)
        });
    }

    const handleInstallationClick = (instId) => {
        setIsLoading(true);
        loadCurrentInstallation(structure.id, instId, () => {
            setIsLoading(false);
        });
    };

    const viewTournee = (tournee) => {
        return (<>
            <div className="flex justify-content-between align-items-center flex-column lg:flex-row">
                <h5>
                    <div>
                        <i className="fa-light fa-calendar mr-2"></i>
                        {parseDate(tournee.date)}
                        <div><i className="fa-light fa-clock mr-2"></i>{I18n.get("firstAppointment") + " : " + parseTime(tournee.meetings[0].debutReception)}</div>
                    </div>
                </h5>
                {!tournee.validee && <div className="tournee-status tournee-status-attente w-max mb-2">{I18n.get("nonConfirme")}</div>}
                {tournee.validee && <div className="tournee-status tournee-status-validee w-max mb-2">{I18n.get("confirme")}</div>}
                <div className="flex flex-row">
                    <Button
                        onClick={() => setPrintMode(tournee)}
                        severity="info"
                        icon="pi pi-print"
                        outlined
                        rounded
                        className="mr-2"

                    />
                    {!tournee.validee && <Button
                        icon="pi pi-pencil"
                        onClick={() => selectedTournee.set(JSON.parse(JSON.stringify(tournee)))}
                        severity="info"
                        outlined
                        rounded
                        className="mr-2"
                    />}
                    {!tournee.validee && <Button
                        icon="pi pi-check"
                        onClick={() => confirmValideTournee(tournee)}
                        severity="success"
                        outlined
                        rounded
                        className=""
                    />}
                </div>

            </div>
            <hr />
            <div className="flex flex-column">
                {tournee.meetings.map((meeting, index) => (
                    <button
                        key={meeting.installationId}
                        className="text-left pb-2 border-none bg-transparent"
                        onClick={() => handleInstallationClick(meeting.installationId)}
                        style={{ cursor: 'pointer' }}
                    >
                        <InstallationCard
                            installation={meeting}
                            mode="tournee"
                            viewMode={true}
                            first={index === 0}
                        />
                    </button>
                ))}
            </div>
        </>)
    }

    const productTemplate = (tournee) => {
        return (
            <Card className="m-2 w-full text-lg h-full ">
                {viewTournee(tournee)}
            </Card>
        );
    }

    const skeletonTournees = () => {
        return <div className="flex flex-column gap-2">
            <Skeleton height="15em"></Skeleton>
            <Skeleton height="15rem"></Skeleton>
            <Skeleton height="15rem"></Skeleton>
        </div>
    }

    const updateFilter = (key, value) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [key]: value
        }));
    };

    const dateTemplate = (date) => {
        let goodDate = false
        tourneeDates.forEach(tourneeDate => {
            if (tourneeDate === new Date(date.year + "-" + (date.month + 1) + "-" + date.day).toDateString()) {
                goodDate = true
            }
        })
        return goodDate ?
            <span className="bg-blue-400 text-white flex align-items-center justify-content-center border-circle" style={{ width: '2rem', height: '2rem' }}>
                {date.day}
            </span> :
            <span>{date.day}</span>
    }


    const filtersView = () => {
        return (
            <div className="flex flex-column pt-1 gap-2">
                <div className="border-100 border-1px border-round p-2 surface-200 flex flex-row align-items-center">
                    {I18n.get("date")}
                    {filters.date && <Button
                        icon="pi pi-times"
                        className="p-button-text h-1rem w-1rem ml-4"
                        onClick={() => updateFilter('date', null)}
                    />}
                </div>
                <Calendar
                    value={filters.date}
                    onChange={(e) => updateFilter('date', e.value)}
                    placeholder={I18n.get("all")}
                    className="w-full pb-3"
                    inline
                    locale={I18n.get("currentlanguage")}
                    dateTemplate={dateTemplate}
                />
                <div className="flex flex-column gap-2">
                    <div className="border-100 border-1px border-round p-2 surface-200">
                        {I18n.get("status")}
                    </div>
                    <div className="flex flex-row align-items-center gap-2">
                        <RadioButton value='' checked={filters.status === ''} onChange={(e) => updateFilter('status', e.value)} />
                        <label>
                            {I18n.get("all")}
                        </label>
                    </div>
                    <div className="flex flex-row align-items-center gap-2">
                        <RadioButton value={true} checked={filters.status === true} onChange={(e) => updateFilter('status', e.value)} />
                        <label>
                            <i className="fa-solid fa-circle text-green-500 mr-2" />
                            {I18n.get("confirme")}
                        </label>
                    </div>
                    <div className="flex flex-row align-items-center gap-2">
                        <RadioButton value={false} checked={filters.status === false} onChange={(e) => updateFilter('status', e.value)} />
                        <label>
                            <i className="fa-solid fa-circle text-yellow-500 mr-2" />
                            {I18n.get("nonConfirme")}
                        </label>
                    </div>
                </div>
            </div>
        )
    }

    const filterTournees = (tournees) => {
        return tournees.filter(tournee => {
            const dateMatch = !filters.date || new Date(tournee.date).toDateString() === filters.date.toDateString();
            const statusMatch = filters.status === '' || tournee.validee === filters.status;
            const searchMatch = filters.search === '' ||
                tournee.meetings.some(meeting => meeting.nom.toLowerCase().includes(filters.search.toLowerCase()) ||
                    meeting.referenceExterne.toString().includes(filters.search.toLowerCase()) ||
                    meeting.localite.toLowerCase().includes(filters.search.toLowerCase()) ||
                    meeting.adresse.toLowerCase().includes(filters.search.toLowerCase()));
            return dateMatch && statusMatch && searchMatch;
        });
    };

    const printView = () => {
        const tournee = printMode;
        setTimeout(() => {
            window.print();
            setPrintMode(null);
        }, 1000);
        return tournee && <Sidebar visible={printMode !== null} onHide={() => setPrintMode(null)} fullScreen>
            <div className="pl-4 pt-6">
                <h5 className="flex justify-content-between pb-2">
                    <div className="flex flex-column">
                        <span className="flex">
                            <i className="fa-light fa-calendar mr-2"></i>
                            {I18n.get("tournees") + " " + I18n.get("du") + " " + parseDate(tournee.date)}
                        </span>
                        <span className="flex">
                            <i className="fa-light fa-clock mr-2"></i>
                            {I18n.get("firstAppointment") + " : " + parseTime(tournee.meetings[0].debutReception)}
                        </span>
                    </div>
                    <div className="flex align-items-center text-3xl underline">
                        {tournee.validee ? I18n.get("confirme") : I18n.get("nonConfirme")}
                    </div>
                </h5>
                {
                    tournee?.meetings.filter(m => !m.deleted).map((item) => (
                        <InstallationCard
                            key={item.referenceExterne}
                            installation={item}
                            mode="tournee"
                            printMode={true}
                            viewMode={true}
                            dragging={false}
                        />
                    ))
                }
            </div >
        </Sidebar >
    }


    const listTourneesView = () => {
        return (
            <div className="card">
                {(selectedTournee.get().meetings?.length > 0 &&
                    <ManageTournee showNotif={showNotif} saveTournee={modifyTournee} onprintTournee={printView} />)
                    || <div>
                        <h5>{I18n.get("mesTournees")}</h5>
                        {isLoading ? skeletonTournees() : (
                            <>
                                <hr />
                                <ConfirmDialog />
                                <div className="flex flex-column lg:flex-row-reverse">
                                    <div id="filterBloc" className="w-full lg:w-4 mb-3 lg:mb-0 lg:ml-3 order-1 lg:order-2 dynamic-filters filtrer-bloc">
                                        {filtersView()}
                                    </div>
                                    <Divider layout="vertical" className="hidden lg:flex order-2 lg:order-2" />
                                    <div id="filterButtonDiv">
                                        <Button
                                            icon="pi pi-filter"
                                            label={I18n.get("filtrer")}
                                            text
                                            onClick={() => {
                                                document.getElementById("filterBloc").classList.remove("filtrer-bloc");
                                                document.getElementById("filterButtonDiv").classList.add("filtrer-bloc");
                                            }}
                                            className="text-gray-500 filtrer-button"
                                        />
                                    </div>
                                    <div className="w-full lg:w-9 order-3 lg:order-1">
                                        <span className="p-input-icon-left w-full">
                                            <i className="pi pi-search" style={{ left: '0.5rem' }} />
                                            <InputText
                                                value={filters.search || ''}
                                                placeholder={I18n.get("search")}
                                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                                className="w-full pl-5"
                                            />
                                        </span>
                                        <DataView
                                            value={filterTournees(tournees.get())}
                                            itemTemplate={productTemplate}
                                            emptyMessage={I18n.get("aucuneTournee")}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>}
            </div>)
    }

    const saveInstallation = async () => {
        setIsLoading(true);
        try {
            await createInstallation(installation.value);
            showNotif("success", "Installation sauvegardée", "Installation sauvegardée avec succès");
            await loadInstallations(structure.id);
            installation.set({});
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de l'installation:", error);
            showNotif("error", "Erreur lors de la sauvegarde de l'installation", error.message || "Une erreur inattendue s'est produite");
        } finally {
            setIsLoading(false);
            scrollToTop();
        }
    }

    const onCancel = () => {
        installation.set({});
        scrollToTop();
    }


    const installationView = () => {
        return (
            <Installation onSave={saveInstallation} onCancel={onCancel} showNotif={showNotif} />
        );
    }

    return printMode !== null ? printView() : (
        <div>
            <Toast ref={toast} />
            <AddContactDialog autoOpenIfEmpty showNotif={showNotif} />
            {(() => {
                if (installation.get().genreInstallationId)
                    return installationView();
                else
                    return listTourneesView();
            })()}
        </div>
    )
}

export default Tournees;